/**
 * Annotation store backed by `localStorage` with a 7-day TTL.
 *
 * On boot we load whatever is in localStorage and drop anything past its
 * `expiresAt`. Every mutation persists synchronously back to localStorage
 * so the inbox survives page reloads without needing the MCP server.
 *
 * Annotations the user explicitly **promotes** (via `promote()`) get
 * pushed to the MCP HTTP bridge and stamped with `promotedAt` — the local
 * copy stops carrying `expiresAt` so it never expires again.
 */
export const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = "hovercontext.annotations.v1";
const safeWindow = () => typeof window === "undefined" ? null : window;
const loadFromStorage = () => {
    const w = safeWindow();
    if (!w?.localStorage)
        return [];
    try {
        const raw = w.localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (parsed.version !== 1 || !Array.isArray(parsed.items))
            return [];
        const now = Date.now();
        return parsed.items.filter((a) => {
            if (a.promotedAt)
                return true;
            if (!a.expiresAt)
                return true;
            return Date.parse(a.expiresAt) > now;
        });
    }
    catch {
        return [];
    }
};
const saveToStorage = (items) => {
    const w = safeWindow();
    if (!w?.localStorage)
        return;
    try {
        const payload = {
            version: 1,
            items,
            updatedAt: Date.now(),
        };
        w.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    }
    catch {
        // Quota exceeded / disabled localStorage — silently degrade.
    }
};
export class AnnotationStore {
    items;
    listeners = new Set();
    maxItems;
    ttlMs;
    persist;
    constructor(options = {}) {
        this.maxItems = options.maxItems ?? 200;
        this.ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
        this.persist = options.persist ?? true;
        this.items = this.persist ? loadFromStorage() : [];
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    list() {
        return this.items;
    }
    count() {
        return this.items.length;
    }
    pending() {
        return this.items.filter((a) => a.status === "pending");
    }
    add(annotation) {
        const stamped = annotation.expiresAt
            ? annotation
            : {
                ...annotation,
                expiresAt: new Date(Date.now() + this.ttlMs).toISOString(),
            };
        this.items = [stamped, ...this.items].slice(0, this.maxItems);
        this.flush();
    }
    update(id, patch) {
        let updated = null;
        this.items = this.items.map((a) => {
            if (a.id !== id)
                return a;
            updated = { ...a, ...patch };
            return updated;
        });
        if (updated)
            this.flush();
        return updated;
    }
    setStatus(id, status) {
        return this.update(id, { status });
    }
    remove(id) {
        const before = this.items.length;
        this.items = this.items.filter((a) => a.id !== id);
        if (this.items.length !== before)
            this.flush();
    }
    clear() {
        if (this.items.length === 0)
            return;
        this.items = [];
        this.flush();
    }
    /**
     * Promote an annotation to the MCP HTTP bridge. On success the local
     * copy is stamped `promotedAt` and `expiresAt` is removed so it stops
     * counting down.
     */
    async promote(id, mcpBaseUrl) {
        const annotation = this.items.find((a) => a.id === id);
        if (!annotation)
            return null;
        try {
            const res = await fetch(`${mcpBaseUrl}/annotations`, {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(annotation),
            });
            if (!res.ok)
                return null;
        }
        catch {
            return null;
        }
        const next = {
            ...annotation,
            promotedAt: new Date().toISOString(),
        };
        // Drop the expiresAt by recreating without it.
        const nextWithoutExpiry = (() => {
            const clone = { ...next };
            delete clone["expiresAt"];
            return clone;
        })();
        this.items = this.items.map((a) => (a.id === id ? nextWithoutExpiry : a));
        this.flush();
        return nextWithoutExpiry;
    }
    /**
     * Returns the earliest `expiresAt` across non-promoted annotations,
     * or `null` when nothing is scheduled to expire.
     */
    nextExpiry() {
        let earliest = null;
        for (const a of this.items) {
            if (a.promotedAt)
                continue;
            if (!a.expiresAt)
                continue;
            if (!earliest || a.expiresAt < earliest.at) {
                earliest = { id: a.id, at: a.expiresAt };
            }
        }
        return earliest;
    }
    /** Forget every annotation including the localStorage copy. */
    wipeStorage() {
        const w = safeWindow();
        if (w?.localStorage) {
            try {
                w.localStorage.removeItem(STORAGE_KEY);
            }
            catch {
                /* ignore */
            }
        }
        this.items = [];
        this.emit();
    }
    flush() {
        if (this.persist)
            saveToStorage(this.items);
        this.emit();
    }
    emit() {
        for (const listener of Array.from(this.listeners)) {
            try {
                listener(this.items);
            }
            catch {
                /* ignore listener failures */
            }
        }
    }
}
//# sourceMappingURL=annotationStore.js.map